import { ConfirmPercentageDialog } from 'app/src/features/UserJoin/components/ConfirmPercentageDialog/ConfirmPercentageDialog'
import { JoinError } from 'app/src/features/UserJoin/components/Error/JoinError'
import { FirstStep } from 'app/src/features/UserJoin/components/FirstStep'
import { JoinSuccess } from 'app/src/features/UserJoin/components/Success/JoinSuccess'
import { UnjoinSuccess } from 'app/src/features/UserJoin/components/Success/UnjoinSuccess'
import { UserJoinConfirm } from 'app/src/features/UserJoin/components/UserJoinConfirm'
import type { SubscriptionSimulationInfoType } from 'app/src/model'
import { membershipApi } from 'app/src/services/welfareportal'
import { PageWidget } from 'app/src/shared/components'
import { calculateSimulationInfo, getFromSessionStorage } from 'app/src/shared/helpers'
import { usePageTitle, useTranslation } from 'app/src/shared/hooks'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect, useState, type ReactElement } from 'react'

export interface PercentageSimulationData {
  bonusAmount?: number
  simulationData?: SubscriptionSimulationInfoType
}

export type JoinStep = 'overview' | 'confirm' | 'success' | 'error' | 'unjoinSuccess'

export function UserJoin(): ReactElement {
  const { t } = useTranslation('joinFunnel')

  const campaignStorage = getFromSessionStorage('selectedCampaign') ?? '{}'
  const membershipId = JSON.parse(campaignStorage).membershipId
  const membershipResponse = membershipApi.useMembershipGet<membershipApi.MembershipExtended>({
    pathParams: { membershipId: membershipId },
  })

  usePageTitle(t('JoinFunnel.Common.titleText'))

  const [percentageSimulationData, setPercentageSimulationData] = useState<PercentageSimulationData>()
  const [chosenPercentage, setChosenPercentage] = useState<number>(1)
  const [currentStep, setCurrentStep] = useState<JoinStep>()
  const [confirmPercentageDialogVisible, setConfirmPercentageDialogVisible] = useState<boolean>(false)

  useEffect(() => {
    if (currentStep === 'success' || currentStep === 'unjoinSuccess') {
      return
    }
    if (membershipResponse.data !== undefined) {
      if (membershipResponse.data.membershipInfo.welfarePercentage == 0) {
        //Welfare percentage = 0 => simulation with 100%
        const simulationData: PercentageSimulationData = {
          bonusAmount: membershipResponse.data.membershipInfo.bonusAmount,
          simulationData: calculateSimulationInfo(1, membershipResponse.data),
        }
        setChosenPercentage(1)
        setPercentageSimulationData(simulationData)
        setCurrentStep('overview')
      }
      if (membershipResponse.data.membershipInfo.welfarePercentage > 0) {
        const simulationData: PercentageSimulationData = {
          bonusAmount: membershipResponse.data.membershipInfo.bonusAmount,
          simulationData: calculateSimulationInfo(
            membershipResponse.data.membershipInfo.welfarePercentage,
            membershipResponse.data,
          ),
        }
        setPercentageSimulationData(simulationData)
        setChosenPercentage(membershipResponse.data.membershipInfo.welfarePercentage)
        setCurrentStep('confirm')
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipResponse.data])

  useEffect(() => {
    if (membershipResponse.data !== undefined) {
      const simulationData: PercentageSimulationData = {
        bonusAmount: membershipResponse.data?.membershipInfo.bonusAmount,
        // chosenPercentage: undefined,
        simulationData: calculateSimulationInfo(chosenPercentage, membershipResponse.data),
      }
      setPercentageSimulationData(simulationData)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenPercentage])

  const [overviewVisited, setOverviewVisited] = useState<boolean>(false)

  useEffect(() => {
    if (currentStep === 'overview' && !overviewVisited) setOverviewVisited(true)
  }, [currentStep, overviewVisited])

  if (membershipResponse.isLoading || !currentStep)
    return (
      <div className='mx-auto flex'>
        <ProgressSpinner data-testid='loading-element' />
      </div>
    )
  return currentStep === 'overview' ? (
    <PageWidget>
      <FirstStep
        formData={percentageSimulationData}
        setChosenPercentage={setChosenPercentage}
        setConfirmPercentageDialogVisible={setConfirmPercentageDialogVisible}
        onNext={() => {
          setCurrentStep('confirm')
        }}
        chosenPercentage={chosenPercentage}
      ></FirstStep>
      {confirmPercentageDialogVisible && membershipResponse.data && (
        <ConfirmPercentageDialog
          membershipData={membershipResponse.data}
          visible={confirmPercentageDialogVisible}
          setChosenPercentage={setChosenPercentage}
          chosenPercentage={chosenPercentage}
          onCancel={() => {
            setConfirmPercentageDialogVisible(false)
          }}
          onConfirm={() => {
            setConfirmPercentageDialogVisible(false)
          }}
          percentages={membershipResponse.data?.welfareCampaign.configuration.joinConfig?.joinPercentages}
        />
      )}
    </PageWidget>
  ) : currentStep === 'confirm' ? (
    <>
      <UserJoinConfirm
        formData={percentageSimulationData}
        chosenPercentage={chosenPercentage}
        userInfoPercentage={membershipResponse.data?.membershipInfo.welfarePercentage}
        userStatus={membershipResponse.data?.membershipInfo.status}
        overviewVisited={overviewVisited}
        setCurrentStep={(step: JoinStep) => setCurrentStep(step)}
        setConfirmPercentageDialogVisible={setConfirmPercentageDialogVisible}
      ></UserJoinConfirm>
    </>
  ) : currentStep === 'success' ? (
    <JoinSuccess />
  ) : currentStep === 'unjoinSuccess' ? (
    <UnjoinSuccess />
  ) : currentStep === 'error' ? (
    <JoinError />
  ) : (
    <></>
  )
}
